import { Address } from "@redotech/account-common/address";
import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { PurchaseLabelType } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import { useRequiredContext } from "@redotech/react-util/context";
import { Rate } from "@redotech/redo-fulfillment-carrier-common/shipment";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { useQuery } from "@tanstack/react-query";

export interface LabelRatesResult {
  rates: Rate[];
  originalLabelCost: number;
}

export function getLabelRatesQueryKey(teamId: string, returnId: string) {
  return ["labelRates", teamId, returnId] as const;
}

interface UseLabelRatesParams {
  teamId: string;
  returnId: string;
  fromAddress: Address | undefined;
  toAddress: Address | undefined;
  weight: { value: number; unit: WeightUnit };
  originalShipmentId: string | undefined;
  labelType: PurchaseLabelType;
  isReShipment?: boolean;
}

export function useLabelRates({
  teamId,
  returnId,
  fromAddress,
  toAddress,
  weight,
  originalShipmentId,
  labelType,
  isReShipment = false,
}: UseLabelRatesParams) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const enabled =
    !!teamId && !!returnId && !!fromAddress && !!toAddress && !!rpcClient;

  return useQuery<LabelRatesResult>({
    queryKey: [
      ...getLabelRatesQueryKey(teamId, returnId),
      fromAddress ? JSON.stringify(fromAddress) : null,
      toAddress ? JSON.stringify(toAddress) : null,
      weight.value,
      weight.unit,
      originalShipmentId ?? null,
      labelType,
      isReShipment,
    ] as const,
    queryFn: async ({ signal }): Promise<LabelRatesResult> => {
      if (!fromAddress || !toAddress) {
        return { rates: [], originalLabelCost: 0 };
      }
      return rpcClient.getLabelRates(
        {
          teamId,
          returnId,
          fromAddress,
          toAddress,
          weight: { value: weight.value, unit: weight.unit },
          originalShipmentId,
          labelType,
          isReShipment,
        },
        { signal },
      );
    },
    enabled,
  });
}
